import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Sparkles,
  FolderOpen,
  X,
  LogOut,
  User,
  Crown,
  Settings
} from 'lucide-react';
import { logout } from '../../redux/slices/userSlice';
import { fetchMySubscription } from '../../redux/slices/paymentSlice';
import Logo from '../Logo';
import UserAvatar from '../Common/UserAvatar';
import { getPlanDetails } from '../../utils/planUtils';

const Sidebar = ({ activeView = 'dashboard', setActiveView, isMobileOpen, setIsMobileOpen }) => {
  const { totalAnalyses } = useSelector((state) => state.analysis);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const { subscription } = useSelector((state) => state.payment);
  console.log('Sidebar render - isAuthenticated:', isAuthenticated);
  console.log('Sidebar render - user details:', user);

  // Fetch subscription on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchMySubscription());
    }
  }, [isAuthenticated, dispatch]);

  const renderPlanBadge = () => {
    const details = getPlanDetails(subscription?.plan?.name, subscription?.status);
    const planName = details.label;
    const isPro = planName.includes('Pro');

    if (isPro) {
      return (
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold shadow-sm text-[10px] uppercase tracking-wide mt-1 ${planName.includes('Yearly') ? 'bg-gradient-to-r from-blue-400 to-indigo-600' : 'bg-gradient-to-r from-emerald-400 to-teal-600'} text-white shadow-indigo-200/50`}>
          <Crown className="w-3 h-3 fill-white/20 text-white" />
          <span className="drop-shadow-sm">{planName}</span>
        </div>
      );
    }

    return (
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg font-semibold text-[10px] uppercase tracking-wider mt-1 ${details.colors}`}>
        <span>{planName}</span>
      </div>
    );
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Overview & insights',
      color: 'blue',
    },
    {
      id: 'search',
      label: 'New Analysis',
      icon: Sparkles,
      description: 'Analyze property',
      color: 'purple',
    },
    {
      id: 'list',
      label: 'All Properties',
      icon: FolderOpen,
      description: 'View all analyses',
      color: 'emerald',
      badge: totalAnalyses || 0,
    },
    {
      id: 'shared',
      label: 'Shared Properties',
      icon: User,
      description: 'Sent & Received',
      color: 'blue',
    },
    {
      id: 'billing',
      label: 'Subscription',
      icon: Crown,
      description: 'Plans & Billing',
      color: 'amber',
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'Account & Password',
      color: 'slate',
    },
  ];

  const handleLogoClick = () => {
    navigate('/');
    setIsMobileOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    window.location.href = '/';
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-80 bg-white/80 backdrop-blur-xl border-r border-white/40 transform transition-all duration-300 ease-in-out shadow-2xl shadow-indigo-500/5 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
      >
        {/* Logo Header */}
        {/* Logo Header - Enterprise Prism Integration */}
        <div className="h-20 px-6 flex items-center justify-between border-b border-slate-200 bg-white/50 backdrop-blur-sm">
          <button
            onClick={handleLogoClick}
            className="flex items-center -ml-2"
          >
            {/* Precision Logo Component */}
            <Logo iconOnly={false} className="scale-75 origin-left h-auto" />
          </button>

          {/* Mobile Close Button */}
          <button
            onClick={() => setIsMobileOpen(false)}
            className="lg:hidden text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 p-1.5 rounded-lg border border-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="py-10 space-y-5">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;

            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveView(item.id);
                  setIsMobileOpen(false);
                }}
                className={`relative w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 group ${isActive
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-sm'
                  : 'bg-white hover:bg-slate-50 border-2 border-slate-100 hover:border-slate-200'
                  }`}
              >
                <div className="flex items-center space-x-4">
                  {/* Icon Container */}
                  <div className={`flex-shrink-0 rounded-lg p-2.5 transition-all duration-200 ${isActive
                    ? item.color === 'blue' ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-md shadow-blue-500/30' :
                      item.color === 'purple' ? 'bg-gradient-to-br from-purple-500 to-purple-600 shadow-md shadow-purple-500/30' :
                        item.color === 'amber' ? 'bg-gradient-to-br from-amber-500 to-amber-600 shadow-md shadow-amber-500/30' :
                          'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-md shadow-emerald-500/30'
                    : 'bg-slate-100 group-hover:bg-slate-200'
                    }`}>
                    <Icon className={`w-5 h-5 transition-all duration-200 ${isActive ? 'text-white' : 'text-slate-600 group-hover:text-slate-700'
                      }`} />
                  </div>

                  {/* Text Content */}
                  <div className="text-left">
                    <h3 className={`text-sm font-bold mb-0.5 transition-colors duration-200 ${isActive ? 'text-slate-900' : 'text-slate-700 group-hover:text-slate-900'
                      }`}>
                      {item.label}
                    </h3>
                    <p className={`text-xs transition-colors duration-200 ${isActive ? 'text-slate-600' : 'text-slate-500 group-hover:text-slate-600'
                      }`}>
                      {item.description}
                    </p>
                  </div>
                </div>

                {/* Badge Count */}
                {item.badge !== undefined && (
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${isActive
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30'
                    : 'bg-slate-200 text-slate-700 group-hover:bg-slate-300'
                    }`}>
                    {item.badge}
                  </div>
                )}

                {/* Active Indicator Bar */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-r-full"></div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom Section - User & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-200 bg-slate-50">
          {/* User Profile */}
          {isAuthenticated && (
            <div className="flex items-center space-x-3 mb-3 p-3 rounded-xl bg-white border border-slate-200 shadow-sm">
              <UserAvatar 
                user={user} 
                size="md" 
                indicator={true}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate tracking-tight">{user?.full_name || user?.email}</p>
                {renderPlanBadge()}
              </div>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-lg bg-white hover:bg-red-50 text-slate-700 hover:text-red-600 border border-slate-200 hover:border-red-200 transition-all duration-200 group"
          >
            <LogOut className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            <span className="text-sm font-semibold">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;